import React, { useState } from 'react';
import { 
  Globe, 
  Award, 
  Shield, 
  Users, 
  UserCheck, 
  Heart,
  Star,
  CheckCircle2,
  Briefcase,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import ApplicationForm from './ApplicationForm';

const ApplicationHub = () => {
  const [showForm, setShowForm] = useState(false);

  if (showForm) {
    return <ApplicationForm onBack={() => setShowForm(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-12">
        
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-700 shadow-2xl animate-in fade-in slide-in-from-bottom-8 duration-700">
          <div className="absolute inset-0 bg-white/10 opacity-20 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]"></div>
          <div className="relative px-8 py-16 sm:px-16 text-center text-white space-y-6">
            <div className="mx-auto w-auto h-auto flex items-center justify-center mb-6">
              <div className="bg-white p-6 rounded-3xl shadow-xl inline-block transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hr-system-repair-1/artifacts/au3caisj_iSource%20LOGO.png" 
                  alt="iSource Asia Logo" 
                  className="h-32 md:h-48 w-auto object-contain"
                />
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
              Welcome to iSource Asia Business Solutions Corporation!
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 max-w-3xl mx-auto font-light">
              Where expertise meets excellence
            </p>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 border border-gray-100 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100 fill-mode-backwards">
          <div className="max-w-3xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
            <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-blue-600 first-letter:float-left first-letter:mr-3">
              We are delighted to welcome you as a new applicant and thank you for your interest in becoming part of our growing team. At iSource Asia, we are committed to delivering excellence and innovation across industries, driven by passionate and highly skilled professionals.
            </p>
          </div>
        </div>

        {/* Mission & Vision Grid */}
        <div className="grid md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-400 fill-mode-backwards">
          {/* Mission Card */}
          <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-600 transition-colors duration-300">
                <Star className="w-8 h-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              To be the top-of-mind choice and preferred provider of products and services across businesses, driven by industry experts.
            </p>
          </div>

          {/* Vision Card */}
          <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 border-indigo-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-600 transition-colors duration-300">
                <Globe className="w-8 h-8 text-indigo-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Our Vision</h2>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              To be present in every business as a premium partner across the Asia Pacific Region.
            </p>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 overflow-hidden relative animate-in fade-in slide-in-from-bottom-8 duration-700 delay-700 fill-mode-backwards">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-indigo-50 rounded-full blur-3xl opacity-50"></div>
          
          <div className="relative">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center p-3 bg-blue-50 rounded-xl mb-4">
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              <ValueCard icon={Shield} title="Integrity" color="text-emerald-600" bg="bg-emerald-50" />
              <ValueCard icon={Award} title="Excellence" color="text-amber-600" bg="bg-amber-50" />
              <ValueCard icon={UserCheck} title="Ownership" color="text-blue-600" bg="bg-blue-50" />
              <ValueCard icon={CheckCircle2} title="Accountability" color="text-indigo-600" bg="bg-indigo-50" />
              <ValueCard icon={Heart} title="Customer-Centricity" color="text-rose-600" bg="bg-rose-50" />
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl relative overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-700 delay-800 fill-mode-backwards">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative max-w-4xl mx-auto space-y-8">
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed font-light">
              As you begin your application journey with us, we encourage you to learn more about our culture, standards, and commitment to delivering quality solutions. We believe that our people are our greatest asset, and we continuously seek individuals who share our passion for excellence and dedication to customer satisfaction.
            </p>
            
            <div className="w-16 h-1 bg-white/20 mx-auto rounded-full"></div>
            
            <div className="space-y-4">
              <p className="text-xl font-medium">
                We look forward to learning more about you and exploring the opportunity to grow together.
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white pt-4">
                "Welcome to iSource Asia Business Solutions Corporation — where expertise meets excellence."
              </p>

              <div className="pt-8">
                <Button 
                  onClick={() => setShowForm(true)}
                  className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6 h-auto rounded-xl font-bold shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Proceed to Application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// Helper Component for Core Values
const ValueCard = ({ icon: Icon, title, color, bg }) => (
  <div className="flex flex-col items-center p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
    <div className={`p-4 rounded-full ${bg} mb-4 group-hover:scale-110 transition-transform duration-300`}>
      <Icon className={`w-8 h-8 ${color}`} />
    </div>
    <h3 className="text-lg font-bold text-gray-800 text-center">{title}</h3>
  </div>
);

export default ApplicationHub;